/** Scale helpers. Owner: core. Implements CORE-210 to CORE-214, CORE-333 and CORE-334. */
import { normalQuantileUpper } from './distributions';
import { measureInfo } from './measures';
import type { AnalysisStudy, Interval, Measure, StudyEffect } from './types';

/**
 * True for comparative measures analysed on the log scale (RR, OR, HR, IRR, GENLOG): displayed as
 * ratios, with "no effect" at 1. PLN and IRLN are pooled on the log scale too but are single-group
 * quantities, so they are not ratio measures.
 */
export function isRatioMeasure(measure: Measure): boolean {
  const m = measureInfo(measure);
  return m.comparative && m.scale === 'log';
}

/**
 * Value of "no effect" on the analysis scale for comparative measures and ZCOR (0: log 1 = 0,
 * RD = MD = SMD = 0, Fisher z of r = 0). Single-group proportions and rates have no null value
 * (`measureInfo(measure).nullValue` is null).
 */
export const NULL_EFFECT = 0;

/** metafor transf.pft: the Freeman-Tukey value of proportion p with sample size n. */
export function freemanTukey(p: number, n: number): number {
  const x = p * n;
  return 0.5 * (Math.asin(Math.sqrt(x / (n + 1))) + Math.asin(Math.sqrt((x + 1) / (n + 1))));
}

/**
 * metafor transf.ipft (Miller 1978): proportion from a Freeman-Tukey value with sample size n,
 * 0 below freemanTukey(0, n) and 1 above freemanTukey(1, n), NaN where the formula has no value.
 */
export function inverseFreemanTukey(x: number, n: number): number {
  if (x > freemanTukey(1, n)) return 1;
  if (x < freemanTukey(0, n)) return 0;
  const s = Math.sin(2 * x);
  const inner = 1 - (s + (s - 1 / s) / n) ** 2;
  return 0.5 * (1 - Math.sign(Math.cos(2 * x)) * Math.sqrt(inner));
}

/**
 * Harmonic mean of the sample sizes of the contributing studies, the `ni` metafor
 * transf.ipft.hm uses to back-transform a pooled Freeman-Tukey value. Null when no contributing
 * study carries a size.
 */
export function harmonicMeanN(studies: readonly StudyEffect[]): number | null {
  let sumInv = 0;
  let count = 0;
  for (const s of studies) {
    if (s.excluded !== null || s.ni === undefined) continue;
    sumInv += 1 / s.ni;
    count += 1;
  }
  return count > 0 ? 1 / (sumInv / count) : null;
}

/**
 * Convert an analysis-scale value to the display scale: exp for log-scale measures, the inverse
 * logit for PLO, tanh for ZCOR, metafor's inverse Freeman-Tukey for PFT (with sample size `n`: the
 * study's `ni` for a study, `harmonicMeanN` for a pooled value; without `n`, its large-sample limit
 * sin(x)^2), and the value itself otherwise.
 */
export function toDisplay(measure: Measure, value: number, n?: number): number {
  switch (measureInfo(measure).scale) {
    case 'identity':
      return value;
    case 'log':
      return Math.exp(value);
    case 'logit':
      return 1 / (1 + Math.exp(-value));
    case 'fisher_z':
      return Math.tanh(value);
    case 'freeman_tukey':
      return n === undefined ? Math.sin(value) ** 2 : inverseFreemanTukey(value, n);
  }
}

export function intervalToDisplay(measure: Measure, interval: Interval, n?: number): Interval {
  return { lower: toDisplay(measure, interval.lower, n), upper: toDisplay(measure, interval.upper, n) };
}

/** True when the interval excludes the null effect (0 on the analysis scale). */
export function excludesNull(interval: Interval): boolean {
  return interval.lower > NULL_EFFECT || interval.upper < NULL_EFFECT;
}

/**
 * excludesNull for a measure: always false for measures without a null value (proportions and
 * rates), since there is no comparison to make.
 */
export function excludesNullFor(measure: Measure, interval: Interval): boolean {
  const nullValue = measureInfo(measure).nullValue;
  if (nullValue === null) return false;
  return interval.lower > nullValue || interval.upper < nullValue;
}

/**
 * Standard error on the analysis scale from a reported confidence interval (CORE-321): for a ratio
 * (`log = true`) (ln upper - ln lower) / (2 z), otherwise (upper - lower) / (2 z), with z the
 * standard normal quantile of the interval's level (1.959964 for 95%). Cochrane Handbook 6.3.1
 * and 6.3.2. Returns NaN when the bounds cannot give a positive SE.
 */
export function seFromConfidenceInterval(lower: number, upper: number, level: number, log: boolean): number {
  if (!(level > 0 && level < 1)) return NaN;
  const lo = log ? Math.log(lower) : lower;
  const hi = log ? Math.log(upper) : upper;
  const se = (hi - lo) / (2 * normalQuantileUpper((1 - level) / 2));
  return Number.isFinite(se) && se > 0 ? se : NaN;
}

/** Participants a study row represents, or null when it does not say (rates; reported without n). */
export function studySize(study: AnalysisStudy): number | null {
  switch (study.kind) {
    case undefined:
    case 'binary':
      return study.total1 + study.total2;
    case 'continuous':
      return study.n1 + study.n2;
    case 'proportion':
      return study.total;
    case 'correlation':
      return study.n;
    case 'reported':
      return study.n ?? null;
    case 'rate':
      return null;
  }
}
